import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import EventIcon from "@mui/icons-material/Event";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCityDisplay } from "../utils/city";

const getStatusChipColor = (paymentStatus) => {
  switch (paymentStatus) {
    case "paid":
      return "success";
    case "pending":
    case "not_paid":
      return "warning";
    case "processing":
    case "refunding":
      return "info";
    case "refunded":
      return "info";
    case "cancelled":
    case "failed":
      return "error";
    default:
      return "default";
  }
};

const PAYMENT_STATUS_I18N = {
  paid: "status.paid",
  not_paid: "status.not_paid",
  pending: "status.pending",
  processing: "status.processing",
  refunding: "status.refunding",
  refunded: "status.refunded",
  failed: "status.failed",
  cancelled: "status.cancelled",
  no_show: "status.no_show",
};

const BookingCard = ({ booking }) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleDetailsClick = () => {
    navigate(`/booking/${booking.id}`);
  };

  const fromDt = booking.from?.departure_datetime
    ? dayjs(booking.from.departure_datetime)
    : booking.date
      ? dayjs(booking.date, "DD.MM.YYYY")
      : null;
  const toDt = booking.to?.arrival_datetime
    ? dayjs(booking.to.arrival_datetime)
    : null;

  // Own trips need departure; partner sales may omit arrival — still show the card.
  if (!fromDt) return null;

  const isPartner =
    booking.source === "contrabus_sale" ||
    booking.provider === "partner" ||
    booking.provider === "contrabus";
  const isCancelledLike = [
    "cancelled",
    "refunded",
    "refunding",
    "failed",
    "no_show",
  ].includes(booking.payment_status);
  const isUpcoming =
    !isCancelledLike &&
    (booking.trip_status === "scheduled" || booking.trip_status === "Відбудеться");
  const paymentStatusKey = PAYMENT_STATUS_I18N[booking.payment_status];
  const paymentStatusLabel = paymentStatusKey
    ? t(paymentStatusKey)
    : booking.payment_status_display ?? booking.payment_status;

  const tripChipLabel = isCancelledLike
    ? paymentStatusLabel
    : isUpcoming
      ? t("bookingCard.upcomingTrip")
      : t("bookingCard.tripCompleted");
  const tripChipColor = isCancelledLike
    ? getStatusChipColor(booking.payment_status)
    : isUpcoming
      ? "primary"
      : "default";

  return (
    <Card
      component="article"
      aria-label={t("bookingCard.ariaLabel", { from: getCityDisplay(booking.from?.city), to: getCityDisplay(booking.to?.city), price: booking.price })}
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          boxShadow: theme.shadows[4],
          borderColor: theme.palette.primary.light,
        },
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2, sm: 3 },
          "&:last-child": { pb: { xs: 2, sm: 3 } },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 2.5, sm: 3 },
          }}
        >
          {/* Відправлення */}
          <Box sx={{ flex: { sm: 1 }, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography
                component="span"
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {fromDt.format("HH:mm")}
              </Typography>
              <Chip
                size="small"
                icon={<EventIcon sx={{ fontSize: 14 }} />}
                label={fromDt.format("DD.MM.YYYY")}
                sx={{
                  height: 24,
                  fontSize: "0.75rem",
                  bgcolor: "action.hover",
                  "& .MuiChip-icon": { color: "primary.main" },
                }}
              />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.5 }}>
              {getCityDisplay(booking.from?.city)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
              {booking.from?.address}
            </Typography>
          </Box>

          {/* Маршрут: статус поїздки + іконка */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flex: { sm: "0 0 auto" },
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: 200 },
            }}
          >
            <Chip
              size="small"
              label={tripChipLabel}
              color={tripChipColor}
              sx={{ mb: 1, fontWeight: 600, whiteSpace: "nowrap" }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  borderRadius: 1,
                  bgcolor: "divider",
                }}
              />
              <DirectionsBusIcon
                sx={{ color: "primary.main", fontSize: 28 }}
                aria-hidden
              />
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  borderRadius: 1,
                  bgcolor: "divider",
                }}
              />
            </Box>
            {!isCancelledLike && (
              <Chip
                size="small"
                label={paymentStatusLabel}
                color={getStatusChipColor(booking.payment_status)}
                sx={{ mt: 1, fontWeight: 500 }}
              />
            )}
          </Box>

          {/* Прибуття */}
          <Box sx={{ flex: { sm: 1 }, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography
                component="span"
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {toDt ? toDt.format("HH:mm") : "—"}
              </Typography>
              {toDt ? (
                <Chip
                  size="small"
                  icon={<EventIcon sx={{ fontSize: 14 }} />}
                  label={toDt.format("DD.MM.YYYY")}
                  sx={{
                    height: 24,
                    fontSize: "0.75rem",
                    bgcolor: "action.hover",
                    "& .MuiChip-icon": { color: "primary.main" },
                  }}
                />
              ) : null}
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.5 }}>
              {getCityDisplay(booking.to?.city)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
              {booking.to?.address}
            </Typography>
            {isPartner && booking.carrier ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {booking.carrier}
              </Typography>
            ) : null}
          </Box>

          {/* Ціна та дія */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "flex-start", sm: "flex-end" },
              justifyContent: "center",
              flex: { sm: "0 0 auto" },
              minWidth: { sm: 120 },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {t("bookingCard.priceUah", { price: booking.price })}
            </Typography>
            <Button
              variant="contained"
              onClick={handleDetailsClick}
              fullWidth={isMobile}
              sx={{
                mt: 1.5,
                minWidth: 120,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                py: 1,
              }}
            >
              {t("bookingCard.details")}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              {t("bookingCard.seats", { count: booking.seats })}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BookingCard;
